/**
 * Core Layer - Audio Processor
 * Handles audio processing, noise reduction, and audio context management
 * No state management, no business logic - pure audio operations
 */

import pako from "pako/dist/pako.esm.mjs";

const AudioContext = window.AudioContext || window.webkitAudioContext;

/**
 * Create audio context
 */
export function createAudioContext(sampleRate = 48000) {
    return new AudioContext({
        latencyHint: 'interactive',
        // sampleRate, // Commented out to use default
    });
}

/**
 * Reduce noise by checking if audio is above threshold
 */
export function reduceNoise(float32Array) {
    let aboveThreshold = false;
    for (const float of float32Array) {
        if (Math.abs(float) > 0.05) {
            aboveThreshold = true;
            break;
        }
    }
    return aboveThreshold;
}

/**
 * Convert number to ArrayBuffer (little-endian)
 */
export function numberToArrayBuffer(number) {
    const buffer = new ArrayBuffer(4);
    const dataView = new DataView(buffer);
    dataView.setUint32(0, number);
    return reverseArrayBuffer(buffer);
}

/**
 * Reverse ArrayBuffer bytes
 */
export function reverseArrayBuffer(buffer) {
    const view = new Uint8Array(buffer);
    view.reverse();
    return buffer;
}

/**
 * Convert string to ArrayBuffer
 */
export function stringToArrayBuffer(str) {
    const encoder = new TextEncoder();
    const encodedStr = encoder.encode(str);
    return encodedStr.buffer;
}

/**
 * Combine multiple ArrayBuffers into one
 */
export function combineArrayBuffers(buffers) {
    const totalLength = buffers.reduce((acc, curr) => acc + curr.byteLength, 0);
    const combined = new Uint8Array(totalLength);

    let offset = 0;
    for (const buffer of buffers) {
        combined.set(new Uint8Array(buffer), offset);
        offset += buffer.byteLength;
    }

    return combined.buffer;
}

/**
 * Compress audio data using gzip
 */
export function compressAudio(int16Array) {
    return pako.gzip(int16Array);
}

/**
 * Decompress audio data using gunzip
 */
export function decompressAudio(compressedBuffer) {
    return pako.ungzip(compressedBuffer);
}

/**
 * Convert Float32Array to Int16Array
 */
export function float32ToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length);
    float32Array.forEach((float32, index) => {
        int16Array[index] = float32 * 32767;
    });
    return int16Array;
}

/**
 * Convert Int16Array to Float32Array
 */
export function int16ToFloat32(int16Array) {
    const floats = [];
    int16Array.forEach(byte => floats.push(byte / 32767));
    return floats;
}

/**
 * Create audio buffer from float array
 */
export function createAudioBuffer(audioContext, floatArray, sampleRate) {
    const buffer = audioContext.createBuffer(1, floatArray.length, sampleRate);

    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const nowBuffering = buffer.getChannelData(channel);
        for (let i = 0; i < buffer.length; i++) {
            nowBuffering[i] = floatArray[i];
        }
    }

    return buffer;
}

/**
 * Play audio buffer
 */
export function playAudioBuffer(audioContext, buffer, onEnded) {
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);
    source.onended = onEnded;
    source.start();
    return source;
}

/**
 * Set audio output device (speaker)
 */
export async function setAudioOutputDevice(audioContext, deviceId) {
    if (audioContext.setSinkId) {
        const speakerId = deviceId === 'default' ? '' : deviceId;
        await audioContext.setSinkId(speakerId);
    }
}

/**
 * Split buffer into frame info and data
 */
export function splitBufferToInfoAndData(buffer) {
    const frameInfoSize = new Uint32Array(buffer.slice(0, 4))[0];
    const frameInfoBuffer = new Uint8Array(buffer.slice(4, 4 + frameInfoSize));
    const frameInfoStr = new TextDecoder().decode(frameInfoBuffer);
    const frameInfo = JSON.parse(frameInfoStr);

    const dataBuffer = new Uint8Array(buffer.slice(4 + frameInfoSize));
    return {dataBuffer, frameInfo};
}
